import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Stack,
  Typography,
  Skeleton,
  CircularProgress,
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import { accordionStyles, getAccordionSummaryStyles, accordianDetailsStyles } from './styles';
import {
  FileTypes,
  FileUploadStatus,
  useGetWorkpackAttachmentsPerDateQuery,
  useRemoveWorkpackAttachmentsMutation,
  useUploadWorkpackAttachmentsMutation,
  useUpdateWorkpackAttachmentFileStatusMutation,
  GetWorkpackAttachmentsPerDateDocument,
} from '@/__generated__/graphql';
import { useGetStructureId } from '@/hooks/useGetStructureId';
import { useAtomValue } from 'jotai';
import * as kpiState from '@/modules/KpiTracker/state';
import { formatDateToYYYYMMDD } from '../../utils';
import { useSnackBarMessage } from '@/utils/useSnackBarMessage';
import { UploadAttachmentsDialog, KpiAttachment } from './UploadAttachmentsDialog';
import { getCloudfrontUrl } from '@/utils/cloudfront';
import { urlToBase64, downloadFile } from '@/utils';
import { Attachment } from '@/components/shared/AttachmentDisplay/type';
import { useFetchCookies } from '@/hooks/useFetchCookies';
import { MediaViewer } from '@/components/shared/MediaViewer';
import { GenericDialog } from '@/components/shared/GenericDialog';
import { abyssColors } from '@/theme/colors';
import { containedButtonStyle, textButtonStyle } from '@/theme';
import { AbyssIcon } from '@/assets/icons/AbyssIcon';
import { FileDisplay } from '@/components/Analysis/shared/MediaUploadsAndViewer/FileDisplay';
import { KPI_ATTACHMENTS_COLLECTION_NAME } from '../../constants';

export const AttachmentsSection = () => {
  const structureId = useGetStructureId();
  const [attachmentsExpanded, setAttachmentsExpanded] = useState(true);
  const [openUploadDialog, setOpenUploadDialog] = useState(false);
  const [attachments, setAttachments] = useState<KpiAttachment[]>([]);
  const [processedAttachments, setProcessedAttachments] = useState<Attachment[]>([]);
  const [isRemoveDialogOpen, setIsRemoveDialogOpen] = useState(false);
  const [attachmentIndexToRemove, setAttachmentIndexToRemove] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [isRemoving, setIsRemoving] = useState(false);

  const selectedWorkpackId = useAtomValue(kpiState.selectedWorkpackIdForKpiTracker);
  const selectedDate = useAtomValue(kpiState.selectedDate);
  const selectedShift = useAtomValue(kpiState.selectedShift);
  const formattedDate = useMemo(() => formatDateToYYYYMMDD(selectedDate), [selectedDate]);
  const userTimezone = useMemo(() => Intl.DateTimeFormat().resolvedOptions().timeZone, []);
  const { showSnackBar } = useSnackBarMessage({ variant: 'filled' });
  const cookiesFetched = useFetchCookies();

  const {
    data: allAttachmentsData,
    loading: queryLoading,
    refetch: refetchAttachments,
  } = useGetWorkpackAttachmentsPerDateQuery({
    variables: {
      input: {
        workpackId: selectedWorkpackId ?? '',
        logDate: formattedDate,
        structureId: structureId,
        timezone: userTimezone,
        shift: selectedShift,
      },
    },
    skip: !selectedWorkpackId || !structureId,
    fetchPolicy: 'cache-first',
  });

  const processFileToAttachment = useCallback(
    async (file: {
      id?: string | null;
      fileName?: string | null;
      name?: string | null;
      type?: string | null;
      resourcePath?: string | null;
      url?: string | null;
      caption?: string | null;
      uploadStatus?: FileUploadStatus | null;
      uuid?: string | null;
      imagePosition?: number | null;
    }) => {
      const {
        fileName,
        name,
        resourcePath,
        url: fileUrlFromQuery,
        type,
        id,
        uploadStatus,
        caption,
        uuid,
        imagePosition,
      } = file;

      if (!id) {
        return null;
      }

      const fileUrl = fileUrlFromQuery || (resourcePath ? getCloudfrontUrl(resourcePath) : '');

      if (!fileUrl) {
        return null;
      }

      const isImage =
        type &&
        (type.includes('image/') || [FileTypes.Jpeg, FileTypes.Png].includes(type as FileTypes));

      let base64: string | undefined;
      if (isImage && fileUrl) {
        try {
          base64 = await urlToBase64(fileUrl);
        } catch {
          base64 = undefined;
        }
      }

      return {
        id: id,
        name: fileName || name || 'Unknown',
        fileName: fileName || name,
        type: type || 'unknown',
        base64,
        isNew: false,
        caption: caption || undefined,
        url: fileUrl,
        resourcePath: resourcePath || undefined,
        uploadStatus: uploadStatus || FileUploadStatus.Success,
        uuid: uuid || undefined,
        imagePosition: imagePosition === null ? undefined : imagePosition,
      };
    },
    []
  );

  useEffect(() => {
    if (!cookiesFetched || queryLoading || !allAttachmentsData) {
      setLoading(queryLoading);
      return;
    }

    const fetchImageURLs = async () => {
      setLoading(true);
      const files = allAttachmentsData.getWorkpackAttachmentsPerDate || [];
      const validFiles = files.filter(
        ({ id, uploadStatus }) => id && uploadStatus === FileUploadStatus.Success
      );

      if (validFiles.length === 0) {
        setProcessedAttachments([]);
        setLoading(false);
        return;
      }

      try {
        const fetchedAttachments = await Promise.all(
          validFiles.map((file) => processFileToAttachment(file))
        );
        const validFetchedAttachments = fetchedAttachments.filter(Boolean) as Attachment[];

        if (validFetchedAttachments.length > 0) {
          setProcessedAttachments(validFetchedAttachments);
        } else {
          setProcessedAttachments([]);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchImageURLs();
  }, [allAttachmentsData, cookiesFetched, processFileToAttachment, queryLoading]);

  const [uploadAttachments] = useUploadWorkpackAttachmentsMutation();
  const [updateFileStatus] = useUpdateWorkpackAttachmentFileStatusMutation();
  const [removeAttachments] = useRemoveWorkpackAttachmentsMutation();

  const handleAddAttachment = () => {
    setOpenUploadDialog(true);
  };

  const handleDownloadAttachment = useCallback(
    (attachmentIndex: number) => {
      const currentAttachment = processedAttachments[attachmentIndex];
      if (!currentAttachment.url) return;
      downloadFile(currentAttachment.url, currentAttachment.name);
    },
    [processedAttachments]
  );

  const handleRemoveAttachment = useCallback(
    (attachmentIndex: number) => {
      const currentAttachment = processedAttachments[attachmentIndex];
      if (!currentAttachment.id) {
        showSnackBar('Unable to remove attachment: ID not available', 'error');
        return;
      }

      setAttachmentIndexToRemove(attachmentIndex);
      setIsRemoveDialogOpen(true);
    },
    [processedAttachments, showSnackBar]
  );

  const handleConfirmRemove = useCallback(async () => {
    if (attachmentIndexToRemove === null) {
      setIsRemoveDialogOpen(false);
      return;
    }

    const currentAttachment = processedAttachments[attachmentIndexToRemove];
    if (!currentAttachment.id) {
      showSnackBar('Unable to remove attachment: ID not available', 'error');
      setIsRemoveDialogOpen(false);
      setAttachmentIndexToRemove(null);
      return;
    }

    setIsRemoving(true);
    try {
      const result = await removeAttachments({
        variables: {
          input: {
            workpackId: selectedWorkpackId ?? '',
            logDate: formattedDate,
            structureId: structureId,
            timezone: userTimezone,
            fileIds: [currentAttachment.id],
            shift: selectedShift,
            lastModifiedFor: 'Attachment removed successfully',
          },
        },
      });

      if (result.data?.removeWorkpackAttachments) {
        setProcessedAttachments((prev) =>
          prev.filter((attachment) => attachment.id !== currentAttachment.id)
        );
        showSnackBar('Attachment removed successfully', 'success');
        await refetchAttachments();
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to remove attachment';
      showSnackBar(errorMessage, 'error');
    } finally {
      setIsRemoving(false);
      setIsRemoveDialogOpen(false);
      setAttachmentIndexToRemove(null);
    }
  }, [
    selectedShift,
    attachmentIndexToRemove,
    processedAttachments,
    selectedWorkpackId,
    formattedDate,
    structureId,
    userTimezone,
    removeAttachments,
    showSnackBar,
    refetchAttachments,
  ]);

  const handleCancelRemove = useCallback(() => {
    setIsRemoveDialogOpen(false);
    setAttachmentIndexToRemove(null);
  }, []);

  const handleUploadAttachments = async (attachmentsToUpload: KpiAttachment[]) => {
    try {
      await uploadAttachments({
        variables: {
          input: {
            files: attachmentsToUpload
              .filter(({ file, uuid }) => file && uuid)
              .map((attachment) => ({
                uuid: attachment.uuid || '',
                name: attachment.name,
                size: attachment.size || 0,
                mimeType: attachment.type,
              })),
            lastModifiedFor: 'Attachments uploaded to workpack',
            logDate: formattedDate,
            structureId: structureId,
            workpackId: selectedWorkpackId ?? '',
            timezone: userTimezone,
            shift: selectedShift,
          },
        },
        onCompleted: async (data) => {
          const uploadedFiles = data?.uploadWorkpackAttachments ?? [];
          if (uploadedFiles.length === 0) {
            showSnackBar('Failed to get upload URLs', 'error');
            return;
          }

          for (const { url, id, uuid } of uploadedFiles) {
            const attachment = attachmentsToUpload.find((att) => att.uuid === uuid);
            if (!attachment?.file) {
              continue;
            }

            try {
              const uploadResponse = await fetch(url, {
                method: 'PUT',
                headers: { 'Content-Type': attachment.type },
                body: attachment.file,
              });

              if (uploadResponse.status === 200) {
                await updateFileStatus({
                  variables: {
                    input: {
                      workpackId: selectedWorkpackId ?? '',
                      fileId: id,
                      uploadStatus: FileUploadStatus.Success,
                    },
                  },
                  refetchQueries: [GetWorkpackAttachmentsPerDateDocument],
                });
              }
            } catch (error) {
              console.error('Failed to update status', error);
              showSnackBar('Failed to update status', 'error');
            }
          }

          showSnackBar('Attachment(s) uploaded successfully', 'success');
          setAttachments([]);
        },
        onError: (error) => {
          console.error('Failed to upload attachments', error);
          showSnackBar(error.message, 'error');
        },
        refetchQueries: [GetWorkpackAttachmentsPerDateDocument],
      });
    } catch (error) {
      console.error(`Failed to upload attachments: ${error}`);
    }
  };

  return (
    <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Accordion
        expanded={attachmentsExpanded}
        onChange={() => setAttachmentsExpanded(!attachmentsExpanded)}
        disableGutters
        sx={{ ...accordionStyles, pb: 1 }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          sx={getAccordionSummaryStyles(attachmentsExpanded)}
        >
          <Typography
            variant="h5"
            sx={{ fontWeight: 500, color: 'text.primary', fontSize: '1.6rem' }}
          >
            Attachments
          </Typography>
        </AccordionSummary>

        <AccordionDetails sx={accordianDetailsStyles}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {loading && (
              <Stack
                direction="row"
                spacing={2}
                sx={{ overflowX: 'auto', pb: 1 }}
                id="kpi-attachments"
              >
                <Stack
                  alignItems="center"
                  gap="0.4rem"
                  sx={{
                    minWidth: '10rem',
                    flexShrink: 0,
                    pt: '0.6rem',
                    pr: '0.6rem',
                  }}
                >
                  <Skeleton
                    variant="rectangular"
                    animation="wave"
                    sx={{
                      borderRadius: '0.4rem',
                      border: `0.1rem solid ${abyssColors.primary[200]}`,
                    }}
                    width="10rem"
                    height="10rem"
                  />
                  <Skeleton
                    variant="text"
                    animation="wave"
                    sx={{ backgroundColor: 'transparent' }}
                    width="10rem"
                    height={20}
                  />
                </Stack>
                <Stack
                  alignItems="center"
                  gap="0.4rem"
                  sx={{
                    minWidth: '10rem',
                    flexShrink: 0,
                    pt: '0.6rem',
                    pr: '0.6rem',
                  }}
                >
                  <Skeleton
                    variant="rectangular"
                    animation="wave"
                    sx={{
                      borderRadius: '0.4rem',
                      border: `0.1rem solid ${abyssColors.primary[200]}`,
                    }}
                    width="10rem"
                    height="10rem"
                  />
                  <Skeleton
                    variant="text"
                    animation="wave"
                    sx={{ backgroundColor: 'transparent' }}
                    width="10rem"
                    height={20}
                  />
                </Stack>
                <Stack
                  alignItems="center"
                  gap="0.4rem"
                  sx={{
                    minWidth: '10rem',
                    flexShrink: 0,
                    pt: '0.6rem',
                    pr: '0.6rem',
                  }}
                >
                  <Skeleton
                    variant="rectangular"
                    animation="wave"
                    sx={{
                      borderRadius: '0.4rem',
                      border: `0.1rem solid ${abyssColors.primary[200]}`,
                    }}
                    width="10rem"
                    height="10rem"
                  />
                  <Skeleton
                    variant="text"
                    animation="wave"
                    sx={{ backgroundColor: 'transparent' }}
                    width="10rem"
                    height={20}
                  />
                </Stack>
              </Stack>
            )}
            {!loading && (
              <Box
                sx={{
                  border: '0.2rem dashed',
                  borderColor: abyssColors.primary[200],
                  borderRadius: '0.8rem',
                  p: 1.2,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 2,
                  backgroundColor: abyssColors.primary[50],
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    flex: 1,
                  }}
                >
                  <AttachFileIcon sx={{ color: abyssColors.primary[300], fontSize: '2rem' }} />
                  <Typography variant="body2" color={abyssColors.primary[300]}>
                    {processedAttachments.length === 0
                      ? 'No attachments for this date. Upload images or PDFs to attach to this work log.'
                      : 'Upload images or PDFs to attach to this work log.'}
                  </Typography>
                </Box>
                <Button
                  id="kpi-tracker-upload-attachments-button"
                  variant="outlined"
                  startIcon={<AbyssIcon iconName="file-upload" size="md" />}
                  onClick={handleAddAttachment}
                  sx={{ ...textButtonStyle, px: '1.4rem', height: 26, fontSize: '1.2rem' }}
                >
                  Upload
                </Button>
              </Box>
            )}
            {!loading && processedAttachments.length > 0 && (
              <Grid container spacing={2} columns={3} id="kpi-attachments">
                <MediaViewer
                  collectionName={KPI_ATTACHMENTS_COLLECTION_NAME}
                  mediaFiles={processedAttachments.filter(
                    (file) =>
                      file &&
                      (file.type?.includes('image/') ||
                        [FileTypes.Jpeg, FileTypes.Png].includes(file.type as FileTypes))
                  )}
                  handleDownloadAttachment={handleDownloadAttachment}
                  handleRemoveAttachment={handleRemoveAttachment}
                >
                  {processedAttachments
                    ?.filter((file) => !!file)
                    .map((attachment, index) => (
                      <Grid key={attachment.id || attachment.name || `attachment-${index}`}>
                        <FileDisplay
                          collectionName={KPI_ATTACHMENTS_COLLECTION_NAME}
                          selectedAttachment={attachment}
                          selectedAttachmentIndex={index}
                          showRemoveIcon={false}
                          enableUpload={false}
                          handleRemoveAttachment={handleRemoveAttachment}
                          handleDownloadAttachment={handleDownloadAttachment}
                        />
                      </Grid>
                    ))}
                </MediaViewer>
              </Grid>
            )}
          </Box>
        </AccordionDetails>
      </Accordion>

      <UploadAttachmentsDialog
        openDialog={openUploadDialog}
        setOpenDialog={setOpenUploadDialog}
        attachments={attachments}
        setAttachments={setAttachments}
        onUpload={handleUploadAttachments}
      />

      <GenericDialog
        open={isRemoveDialogOpen}
        onClose={handleCancelRemove}
        title="Remove Attachment"
        content={
          <Typography fontWeight={400} fontSize="14px" color={abyssColors.primary[500]}>
            Are you sure you want to remove this attachment?
          </Typography>
        }
        actions={
          <>
            <Button
              variant="text"
              onClick={handleCancelRemove}
              disabled={isRemoving}
              sx={{ color: abyssColors.primary[500] }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleConfirmRemove}
              disabled={isRemoving}
              sx={containedButtonStyle}
              startIcon={isRemoving ? <CircularProgress size={16} color="inherit" /> : undefined}
            >
              {isRemoving ? 'Removing...' : 'Remove'}
            </Button>
          </>
        }
        actionsStyles={{ mr: 3, pb: 3 }}
      />
    </Box>
  );
};
